import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { Datasource, DataSourceStatus } from '@Core/services/proprietary/datasource';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import {
    CancelEditMode,
    DeleteFiling,
    ResetState,
    SetEditMode,
    SetObjectState,
    UpdateDocument
} from './document-detail-object.actions';
import { normalize, rootSchema } from './document-detail-object.normalize';
import { DocumentDetailObjectState } from './document-detail-object.state';

@Injectable()
export class DocumentDetailObjectService implements OnDestroy {
    public dsDocDetailGet: Datasource;
    public dsDocDetailProcess: Datasource;

    dsDocDetailGetStatusSub: Subscription;
    dsDocDetailProcessSub: Subscription;

    constructor(
        private store: Store,
        private app: AppService,
        private ds: DataService,
        private helper: StateHelperService
    ) {
        this.dsDocDetailGet = this.ds.addNewDatasource('Document', 'NO-CACHE');
        this.dsDocDetailProcess = this.ds.addNewDatasource('Document', 'NO-CACHE');
    }

    ngOnDestroy() {
        this.reset();
    }

    reset() {
        if (this.dsDocDetailGetStatusSub) {
            this.dsDocDetailGetStatusSub.unsubscribe();
        }

        if (this.dsDocDetailProcessSub) {
            this.dsDocDetailProcessSub.unsubscribe();
        }

        if (this.dsDocDetailGet) {
            this.ds.deleteDatasource(this.dsDocDetailGet);
        }

        if (this.dsDocDetailProcess) {
            this.ds.deleteDatasource(this.dsDocDetailProcess);
        }

        this.resetState();
    }

    reloadDoc() {
        let docID = this.dsDocDetailProcess.data.ConceptOneReply?.DocumentProcess?.Document?.Doc_ID;
        this.resetState();
        if (docID) {
            this.loadDocumentDetail(docID);
        }
    }

    loadDocumentDetail(docID: any) {
        if (docID) {
            this.dsDocDetailGet.reset();
            if (this.dsDocDetailProcess.error == true) {
                this.dsDocDetailProcess.reset();
            }

            this.dsDocDetailGet.get(
                {
                    json: 'CT2',
                    Doc_ID: docID,
                    View: 'Full',
                    Doc_Format: 'NONE',
                    Reference_Options: 'Entity_Name.ALL;Desc.ALL'
                },
                true,
                false
            );

            this.dsDocDetailGetStatusSub = this.dsDocDetailGet.status$.subscribe((status) => {
                this.docDetailGetStatusUpdated(status);
            });
        }
    }

    docDetailGetStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsDocDetailGetStatusSub) {
                    this.dsDocDetailGetStatusSub.unsubscribe();
                }
                this.load(this.dsDocDetailGet.data);
                break;
            case DataSourceStatus.Error:
                if (this.dsDocDetailGetStatusSub) {
                    this.dsDocDetailGetStatusSub.unsubscribe();
                }
                break;
        }
    }

    load(data: any) {
        let normalizedData = normalize(data, rootSchema);
        this.store.dispatch(new SetObjectState(normalizedData.entities));
    }

    updateDocument(document: any) {
        this.store.dispatch(new UpdateDocument(document));
    }

    deleteFiling(docLinkID: any) {
        this.store.dispatch(new DeleteFiling(docLinkID));
    }

    setEditMode() {
        this.store.dispatch(new SetEditMode());
    }

    cancelEditMode() {
        this.store.dispatch(new CancelEditMode());
    }

    resetState() {
        this.store.dispatch(new ResetState());
    }

    processDocumentDetailUpdates() {
        let updatedDocument = cloneDeep(this.store.selectSnapshot(DocumentDetailObjectState.document));
        let initialDocProtectedInd = this.store.selectSnapshot(DocumentDetailObjectState.initialDocumentProtectedInd);

        if (initialDocProtectedInd == 'Y') {
            if (updatedDocument.Ext_Collation_Seq == '') {
                delete updatedDocument.Ext_Collation_Seq;
            }
        } else {
            if (updatedDocument.Ext_Collation_Seq == '') {
                updatedDocument.Ext_Collation_Seq = '0';
            }
        }

        const isFiledDoc = updatedDocument.Filed_Ind == 'Y';
        const isUnfiledDoc = updatedDocument.Filed_Ind == 'N';
        const allowFiledDocDisable = this.app.getSecurityOptionValue('Disable_Filed_Document_Ind') == 'Y' ? true : false;
        const allowUnfiledDocDisable = this.app.getSecurityOptionValue('Disable_Document_Ind') == 'Y' ? true : false;

        if (isFiledDoc) {
            if (!allowFiledDocDisable) {
                delete updatedDocument.Disabled_Ind;
            }
        } else if (isUnfiledDoc) {
            if (!allowUnfiledDocDisable) {
                delete updatedDocument.Disabled_Ind;
            }
        }

        let filings = this.store.selectSnapshot(DocumentDetailObjectState.filings);
        if (!filings) {
            filings = [];
        }

        const documentProcess = {
            Book_Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
            Book_Loc_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID,
            Event: 'Update',
            Document: {
                ...updatedDocument,
                Filing: [...filings]
            }
        };

        const body = {
            '?xml': { '@version': '1.0' },
            ConceptOneRequest: {
                DocumentProcess: {
                    View: 'Full',
                    ...documentProcess
                }
            }
        };

        this.dsDocDetailProcess.process({ json: 'CT2' }, body);

        this.dsDocDetailProcessSub = this.dsDocDetailProcess.status$.subscribe((x) => {
            this.processDocDetailStatusUpdated(x);
        });
    }

    processDocDetailStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsDocDetailProcessSub) {
                    this.dsDocDetailProcessSub.unsubscribe();
                }
                this.reloadDoc();
                break;
            case DataSourceStatus.Error:
                if (this.dsDocDetailProcessSub) {
                    this.dsDocDetailProcessSub.unsubscribe();
                }
                break;
        }
    }
}
